import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import * as moment from 'moment';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';

import {
  VACATION_AVAILABILITY_ADVISORY_REASON_CODES,
  VACATION_AVAILABILITY_CALENDAR_CONFIG,
  VACATION_AVAILABILITY_CALENDAR_KEY,
  VACATION_AVAILABILITY_MONTH_KEYS,
  VACATION_AVAILABILITY_MONTH_UNIT,
  VACATION_AVAILABILITY_PLACEHOLDER_PREFIX,
  VACATION_AVAILABILITY_WEEKDAY_KEYS
} from './vacation-availability-calendar.constants';
import { VacationApiService } from './vacation-api.service';
import {
  VACATION_ERROR_TRANSLATION,
  VACATION_WARNING_TRANSLATION
} from './vacation-booking-panel.constants';
import { VACATION_ERROR_CODE } from './vacation-api.constants';
import {
  VacationAvailabilityCalendarCell,
  VacationAvailabilityDay,
  VacationAvailabilityResponse,
  VacationDateRangeSelection
} from './vacation.models';

@Component({
  selector: 'app-vacation-availability-calendar',
  templateUrl: './vacation-availability-calendar.component.html',
  styleUrls: ['./vacation-availability-calendar.component.css']
})
export class VacationAvailabilityCalendarComponent implements OnInit, OnDestroy {

  @Input() public from = VACATION_AVAILABILITY_CALENDAR_CONFIG.EMPTY_TEXT;
  @Input() public to = VACATION_AVAILABILITY_CALENDAR_CONFIG.EMPTY_TEXT;
  @Input() public minimumDate = VACATION_AVAILABILITY_CALENDAR_CONFIG.EMPTY_TEXT;
  @Input() public maximumDate = VACATION_AVAILABILITY_CALENDAR_CONFIG.EMPTY_TEXT;
  @Output() public selectionChange = new EventEmitter<VacationDateRangeSelection>();

  public readonly weekdayKeys = VACATION_AVAILABILITY_WEEKDAY_KEYS;
  public cells: VacationAvailabilityCalendarCell[] = [];
  public monthKey = VACATION_AVAILABILITY_CALENDAR_CONFIG.EMPTY_TEXT;
  public monthYear: number;
  public loading = false;
  public empty = false;
  public errorKey = VACATION_AVAILABILITY_CALENDAR_CONFIG.EMPTY_TEXT;
  public previousMonthDisabled = true;
  public nextMonthDisabled = false;
  private monthCursor = moment().startOf(VACATION_AVAILABILITY_MONTH_UNIT);
  private currentMonth = moment().startOf(VACATION_AVAILABILITY_MONTH_UNIT);
  private reload$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  constructor(private vacationApiService: VacationApiService) {}

  public ngOnInit(): void {
    this.updateMonthContext();
    this.loadAvailability();
  }

  public ngOnDestroy(): void {
    this.reload$.next();
    this.reload$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public previousMonth(): void {
    if (this.previousMonthDisabled) {
      return;
    }
    this.changeMonth(VACATION_AVAILABILITY_CALENDAR_CONFIG.PREVIOUS_MONTH_STEP);
  }

  public nextMonth(): void {
    if (this.nextMonthDisabled) {
      return;
    }
    this.changeMonth(VACATION_AVAILABILITY_CALENDAR_CONFIG.NEXT_MONTH_STEP);
  }

  public retry(): void {
    this.loadAvailability();
  }

  public selectDay(cell: VacationAvailabilityCalendarCell): void {
    if (!cell.selectable || !cell.date) {
      return;
    }
    this.selectionChange.emit(this.nextSelection(cell.date));
  }

  public trackByCellKey(
    index: number,
    cell: VacationAvailabilityCalendarCell
  ): string {
    return cell.key;
  }

  public trackByTranslationKey(index: number, key: string): string {
    return key;
  }

  private loadAvailability(): void {
    const range = this.visibleRange();
    this.reload$.next();
    this.startLoad();
    this.vacationApiService.getMyAvailability(range.from, range.to)
      .takeUntil(this.reload$)
      .takeUntil(this.destroy$)
      .subscribe(
        response => this.acceptAvailability(response),
        () => this.failAvailability()
      );
  }

  private acceptAvailability(response: VacationAvailabilityResponse): void {
    this.empty = !response.days.length;
    this.cells = this.empty ? [] : this.buildCells(response.days);
    this.loading = false;
  }

  private failAvailability(): void {
    this.loading = false;
    this.errorKey = VACATION_AVAILABILITY_CALENDAR_KEY.LOAD_ERROR;
  }

  private changeMonth(step: number): void {
    if (this.loading) {
      return;
    }
    this.monthCursor.add(step, VACATION_AVAILABILITY_MONTH_UNIT);
    this.updateMonthContext();
    this.loadAvailability();
  }

  private updateMonthContext(): void {
    this.monthKey = VACATION_AVAILABILITY_MONTH_KEYS[this.monthCursor.month()];
    this.monthYear = this.monthCursor.year();
    this.previousMonthDisabled = !this.monthCursor.isAfter(this.currentMonth);
    this.nextMonthDisabled = this.isMaximumMonth();
  }

  private startLoad(): void {
    this.cells = [];
    this.empty = false;
    this.loading = true;
    this.errorKey = VACATION_AVAILABILITY_CALENDAR_CONFIG.EMPTY_TEXT;
  }

  private visibleRange(): VacationDateRangeSelection {
    const monthEnd = this.monthCursor.clone()
      .endOf(VACATION_AVAILABILITY_MONTH_UNIT);
    return {
      from: this.monthCursor.clone().startOf(VACATION_AVAILABILITY_MONTH_UNIT)
        .format(VACATION_AVAILABILITY_CALENDAR_CONFIG.DATE_FORMAT),
      to: this.maximumDate && monthEnd.isAfter(moment(this.maximumDate))
        ? this.maximumDate
        : monthEnd.format(VACATION_AVAILABILITY_CALENDAR_CONFIG.DATE_FORMAT)
    };
  }

  private buildCells(
    days: VacationAvailabilityDay[]
  ): VacationAvailabilityCalendarCell[] {
    const cells = this.placeholderCells();
    days.slice()
      .sort((left, right) => left.date.localeCompare(right.date))
      .forEach(day => cells.push(this.dayCell(day)));
    return cells;
  }

  private placeholderCells(): VacationAvailabilityCalendarCell[] {
    const cells: VacationAvailabilityCalendarCell[] = [];
    const count = this.monthCursor.clone().startOf(VACATION_AVAILABILITY_MONTH_UNIT)
      .isoWeekday() - VACATION_AVAILABILITY_CALENDAR_CONFIG.FIRST_ISO_WEEKDAY;
    for (
      let index = VACATION_AVAILABILITY_CALENDAR_CONFIG.FIRST_INDEX;
      index < count;
      index++
    ) {
      cells.push({
        key: VACATION_AVAILABILITY_PLACEHOLDER_PREFIX + index,
        placeholder: true
      });
    }
    return cells;
  }

  private dayCell(day: VacationAvailabilityDay): VacationAvailabilityCalendarCell {
    const selectable = this.isSelectable(day);
    const advisory = this.isAdvisory(day, selectable);
    const withinAllowedRange = this.isWithinAllowedRange(day.date);
    return {
      key: day.date,
      placeholder: false,
      date: day.date,
      dayNumber: moment(day.date).date(),
      advisory: advisory,
      available: day.available && !advisory && withinAllowedRange,
      selectable: selectable,
      labelKey: this.dayLabelKey(day, selectable, advisory)
    };
  }

  private dayLabelKey(
    day: VacationAvailabilityDay,
    selectable: boolean,
    advisory: boolean
  ): string {
    if (advisory && day.reasonCode) {
      return VACATION_WARNING_TRANSLATION[day.reasonCode] ||
        VACATION_AVAILABILITY_CALENDAR_KEY.DAY_ADVISORY;
    }
    if (day.available && selectable) {
      return VACATION_AVAILABILITY_CALENDAR_KEY.DAY_AVAILABLE;
    }
    return day.reasonCode
      ? VACATION_ERROR_TRANSLATION[day.reasonCode] ||
        VACATION_AVAILABILITY_CALENDAR_KEY.DAY_UNAVAILABLE
      : VACATION_AVAILABILITY_CALENDAR_KEY.DAY_UNAVAILABLE;
  }

  private isAdvisory(day: VacationAvailabilityDay, selectable: boolean): boolean {
    if (!selectable || !day.reasonCode) {
      return false;
    }
    return VACATION_AVAILABILITY_ADVISORY_REASON_CODES.indexOf(day.reasonCode)
      !== VACATION_AVAILABILITY_CALENDAR_CONFIG.NOT_FOUND_INDEX;
  }

  private isSelectable(day: VacationAvailabilityDay): boolean {
    return this.isWithinAllowedRange(day.date)
      && (day.available
        || day.reasonCode === VACATION_ERROR_CODE.SEASON_ALREADY_USED);
  }

  private isMaximumMonth(): boolean {
    return !!this.maximumDate && !this.monthCursor.isBefore(
      moment(this.maximumDate).startOf(VACATION_AVAILABILITY_MONTH_UNIT)
    );
  }

  private isWithinAllowedRange(date: string): boolean {
    return date >= this.minimumDate
      && (!this.maximumDate || date <= this.maximumDate);
  }

  private nextSelection(date: string): VacationDateRangeSelection {
    if (!this.from || this.to || date < this.from) {
      return this.newRange(date);
    }
    return {
      from: this.from,
      to: date
    };
  }

  private newRange(from: string): VacationDateRangeSelection {
    return {
      from: from,
      to: VACATION_AVAILABILITY_CALENDAR_CONFIG.EMPTY_TEXT
    };
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/vacation/vacation-availability-calendar.component.ts